import React, { useState, useEffect, useRef } from "react";
import { openApi } from "../../services/api";
import api from "../../services/api";
import { toast } from "react-toastify";

const DEMO_DURATION_MS = 2 * 60 * 1000; // 2 minutos

const styles = {
  banner: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    background: "linear-gradient(90deg, #6d28d9 0%, #2563eb 100%)",
    color: "#fff",
    padding: "10px 20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    zIndex: 99998,
    fontFamily: "sans-serif",
    fontSize: 14,
    fontWeight: 600,
    boxShadow: "0 -2px 8px rgba(0,0,0,0.3)",
  },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.85)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 999999,
    fontFamily: "sans-serif",
  },
  card: {
    background: "#fff",
    borderRadius: 16,
    padding: "40px 36px",
    width: "100%",
    maxWidth: 440,
    boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
    textAlign: "center",
  },
  input: {
    width: "100%",
    padding: "12px 14px",
    border: "1.5px solid #d1d5db",
    borderRadius: 8,
    fontSize: 15,
    outline: "none",
    marginBottom: 12,
    boxSizing: "border-box",
    fontFamily: "sans-serif",
  },
  btn: {
    width: "100%",
    padding: "14px",
    background: "linear-gradient(90deg, #6d28d9, #2563eb)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
    marginTop: 4,
  },
  btnDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  ctaBtn: {
    background: "#fff",
    color: "#6d28d9",
    border: "none",
    padding: "8px 18px",
    borderRadius: 6,
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 13,
    marginLeft: 12,
  },
};

const DemoBanner = () => {
  const [isDemo, setIsDemo] = useState(false);
  const [remaining, setRemaining] = useState(DEMO_DURATION_MS);
  const [expired, setExpired] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);

  // Signup form state
  const [form, setForm] = useState({ companyName: "", name: "", email: "", password: "", phone: "" });
  const [loading, setLoading] = useState(false);

  // Check demo on mount.
  // React runs child effects BEFORE parent effects, so useAuth (parent)
  // may not have set localStorage yet — we also listen for the custom event.
  useEffect(() => {
    const checkDemo = () => {
      const demo = localStorage.getItem("isDemo");
      const startTime = localStorage.getItem("demoStartTime");
      if (demo === "true" && startTime) {
        setIsDemo(true);
        const elapsed = Date.now() - parseInt(startTime, 10);
        const rem = DEMO_DURATION_MS - elapsed;
        if (rem <= 0) {
          setExpired(true);
          setShowOverlay(true);
          setRemaining(0);
        } else {
          setRemaining(rem);
        }
      }
    };
    checkDemo();
    window.addEventListener("chatprime-demo-ready", checkDemo);
    return () => window.removeEventListener("chatprime-demo-ready", checkDemo);
  }, []);

  // Countdown tick
  useEffect(() => {
    if (!isDemo || expired) return;
    const tick = setInterval(() => {
      const startTime = parseInt(localStorage.getItem("demoStartTime") || "0", 10);
      const elapsed = Date.now() - startTime;
      const rem = DEMO_DURATION_MS - elapsed;
      if (rem <= 0) {
        setRemaining(0);
        setExpired(true);
        setShowOverlay(true);
        clearInterval(tick);
      } else {
        setRemaining(rem);
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [isDemo, expired]);

  const clearDemoFlags = () => {
    localStorage.removeItem("isDemo");
    localStorage.removeItem("demoStartTime");
  };

  const formatTime = (ms) => {
    const totalSec = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!form.companyName || !form.name || !form.email || !form.password || !form.phone) {
      toast.warn("Preencha todos os campos!");
      return;
    }
    setLoading(true);
    try {
      // 1. Criar conta
      await openApi.post("/auth/signup", {
        companyName: form.companyName,
        name: form.name,
        email: form.email,
        password: form.password,
        phone: form.phone,
      });

      // 2. Login automático
      const { data } = await openApi.post("/auth/login", {
        email: form.email,
        password: form.password,
      });

      // 3. Salvar token e autenticar
      localStorage.setItem("login_time", Date.now().toString());
      localStorage.setItem("token", JSON.stringify(data.token));
      api.defaults.headers.Authorization = `Bearer ${data.token}`;
      clearDemoFlags();

      toast.success("Conta criada! Bem-vindo ao ChatPrime 🎉");
      if (typeof window.fbq === 'function') window.fbq('track', 'CompleteRegistration', { content_name: 'ChatPrime Trial', currency: 'BRL', value: 0 });
      // Recarregar para inicializar o AuthContext corretamente
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || "Erro ao criar conta. Tente novamente.";
      toast.error(msg);
      setLoading(false);
    }
  };

  if (!isDemo) return null;

  return (
    <>
      {/* Banner de contagem regressiva */}
      {!expired && (
        <div style={styles.banner}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 18 }}>🎯</span>
            <span>
              <strong>Modo demonstração</strong> — explore a plataforma à vontade!
            </span>
            <span style={{
              background: "rgba(255,255,255,0.2)",
              borderRadius: 4,
              padding: "2px 10px",
              fontSize: 16,
              fontFamily: "monospace",
              letterSpacing: 1,
            }}>
              ⏱ {formatTime(remaining)} restantes
            </span>
          </div>
          <button style={styles.ctaBtn} onClick={() => setShowOverlay(true)}>
            Criar conta grátis →
          </button>
        </div>
      )}

      {/* Overlay de conversão */}
      {showOverlay && (
        <div style={styles.overlay}>
          <div style={styles.card}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🚀</div>
            <h2 style={{ margin: "0 0 8px", fontSize: 22, color: "#1f2937" }}>
              {expired ? "Seu tempo de demonstração acabou!" : "Crie sua conta gratuita"}
            </h2>
            <p style={{ color: "#6b7280", fontSize: 14, margin: "0 0 24px" }}>
              {expired
                ? "Gostou? Crie sua conta agora e ganhe 7 dias grátis para testar tudo!"
                : "Garanta seu acesso com 7 dias gratuitos. Sem cartão de crédito."}
            </p>
            <form onSubmit={handleSignup}>
              <input
                style={styles.input}
                placeholder="Nome da sua empresa"
                value={form.companyName}
                onChange={e => setForm(f => ({ ...f, companyName: e.target.value }))}
              />
              <input
                style={styles.input}
                placeholder="Seu nome completo"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              />
              <input
                style={styles.input}
                type="email"
                placeholder="Seu e-mail"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              />
              <input
                style={styles.input}
                type="password"
                placeholder="Crie uma senha"
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              />
              <input
                style={styles.input}
                placeholder="WhatsApp (ex: 11999990000)"
                value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
              />
              <button
                type="submit"
                style={{ ...styles.btn, ...(loading ? styles.btnDisabled : {}) }}
                disabled={loading}
              >
                {loading ? "Criando sua conta..." : "Criar conta grátis — 7 dias free!"}
              </button>
            </form>
            {!expired && (
              <button
                style={{ marginTop: 14, background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: 13 }}
                onClick={() => setShowOverlay(false)}
              >
                Voltar para demonstração
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default DemoBanner;
